import { Grid } from "@mui/material";
import { FormikTextField, useResizableFieldMaxHeight } from "@shared";
import { FormikProps } from "formik";

import { FormikCounselorSelect } from "@/common/components/counselor-select/FormikCounselorSelect";
import { IOptionItem } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete.types";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDatePicker } from "@/common/components/fields/formik/formik-date-picker/FormikDatePicker";
import { FormikTimePicker } from "@/common/components/fields/formik/formik-time-picker/FormikTimePicker";
import { useTranslation } from "@/translations";

import { CaseNoteFormValues } from "./CaseNoteForm.types";
import { CaseNoteTemplateSelect } from "./components/case-note-template-select/CaseNoteTemplateSelect";

interface Props {
  formik: FormikProps<CaseNoteFormValues>;
  clientId: string;
  typeOptions: IOptionItem[];
  locationOptions: IOptionItem[];
  resultOptions: IOptionItem[];
  appointmentStatusOptions: IOptionItem[];
  isConversationType: (type: string | null) => boolean;
}

export function CaseNoteForm({
  formik,
  clientId,
  typeOptions,
  locationOptions,
  resultOptions,
  appointmentStatusOptions,
  isConversationType,
}: Props) {
  const { t } = useTranslation();
  const { setFieldRef, maxHeight } = useResizableFieldMaxHeight();

  return (
    <Grid container rowSpacing={2} columnSpacing={2} sx={{ pt: 2 }}>
      <Grid size={6}>
        <FormikAutocompleteSelect
          formik={formik}
          fieldName="type"
          label={t("clients.client.caseNotes.form.type")}
          options={typeOptions}
          required
        />
      </Grid>
      <Grid size={3}>
        <FormikDatePicker formik={formik} fieldName="date" label={t("clients.client.caseNotes.form.date")} clearable />
      </Grid>
      <Grid size={3}>
        <FormikTimePicker formik={formik} fieldName="time" label={t("clients.client.caseNotes.form.time")} />
      </Grid>
      <Grid size={6}>
        <CaseNoteTemplateSelect formik={formik} clientId={clientId} />
      </Grid>
      {isConversationType(formik.values.type) && (
        <Grid size={6}>
          <FormikAutocompleteSelect
            formik={formik}
            fieldName="appointmentStatus"
            label={t("clients.client.caseNotes.form.appointmentStatus")}
            options={appointmentStatusOptions}
          />
        </Grid>
      )}
      <Grid size={12}>
        <FormikTextField
          formik={formik}
          fieldName="content"
          label={t("clients.client.caseNotes.form.content")}
          multiline
          rows={8}
          inputRef={setFieldRef}
          sx={{
            "& .MuiInputBase-inputMultiline": { resize: "vertical", maxHeight },
          }}
        />
      </Grid>
      <Grid size={4}>
        <FormikCounselorSelect formik={formik} fieldName="assignedCounselorId" label={t("common.counselor")} />
      </Grid>
      <Grid size={4}>
        <FormikAutocompleteSelect
          formik={formik}
          fieldName="location"
          label={t("clients.client.caseNotes.form.location")}
          options={locationOptions}
        />
      </Grid>
      <Grid size={4}>
        <FormikAutocompleteSelect
          formik={formik}
          fieldName="result"
          label={t("clients.client.caseNotes.form.result")}
          options={resultOptions}
        />
      </Grid>
    </Grid>
  );
}
